<div class="mb-3">
  <h1 class="fs-5 fw-bold mb-0">Hourly statistics</h1>
</div>

<div class="card border-0 shadow mb-3 fade-in-zoom">
  <div class="card-body">
    <div class="row mb-3">
      <div class="col-6">
        <app-date-range-selector #periodSelector [date]="initialPeriod.preset" [zone]="'Europe/Paris'"
          [initialFrom]="initialPeriod.from" [initialTo]="initialPeriod.to"
          (dateChanged)="onPeriodChange(periodSelector)"></app-date-range-selector>
      </div>

      <div class="col-3">
        <label for="comparison" class="form-label fw-bold small">Compare to</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-arrow-left-right"></i></span>
          <select id="comparison" class="form-select" [(ngModel)]="comparison" (ngModelChange)="changeComparison()">
            <option value="none">No comparison</option>
            <option value="previous">Previous period</option>
            <option value="last-week">Same period last week</option>
            <option value="last-year">Same period last year (same weekdays)</option>
            <option value="custom">Custom period</option>
          </select>
        </div>
        @if (comparison !== 'none' && comparison !== 'custom') {
          <div class="form-text compared-period">
            <i class="bi bi-calendar-range me-1"></i>{{ formatPeriod(comparedPeriod) }}
          </div>
        }
      </div>

      <div class="col-3">
        <label for="hourlyTransactions" class="form-label fw-bold small">Show Transactions</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-file-earmark-text"></i></span>
          <select id="hourlyTransactions" class="form-select" [(ngModel)]="transactionDate">
            @for (option of transactionDates; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>
      </div>
    </div>

    <div class="row mb-3" [hidden]="comparison !== 'custom'">
      <div class="col-6">
        <app-date-range-selector #comparedPeriodSelector [date]="initialComparedPeriod.preset" [zone]="'Europe/Paris'"
          [initialFrom]="initialComparedPeriod.from" [initialTo]="initialComparedPeriod.to"
          [label]="'Compared period'" [inputId]="'comparedPeriod'"
          (dateChanged)="onComparedPeriodChange(comparedPeriodSelector)"></app-date-range-selector>
      </div>
    </div>

    <app-stats-filters [initialParams]="initialParams" (ready)="apply()">
      <div class="col-3">
        <label for="hourlyMode" class="form-label fw-bold small">View</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-clock"></i></span>
          <select id="hourlyMode" class="form-select" [(ngModel)]="mode" (ngModelChange)="changeMode()">
            <option value="timeline">Hour by hour (max. {{ maxDays }} days)</option>
            <option value="profile">24h profile (hours of the day)</option>
          </select>
        </div>
      </div>

      <div class="col-3">
        <label for="hourlyValues" class="form-label fw-bold small">Values</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-calculator"></i></span>
          <select id="hourlyValues" class="form-select" [(ngModel)]="valueMode" (ngModelChange)="changeValueMode()"
            [disabled]="mode === 'timeline'">
            <option value="total">Total over the period</option>
            <option value="average">Average per day</option>
          </select>
        </div>
      </div>
    </app-stats-filters>

    @if (periodError) {
      <div class="alert alert-warning small py-2 mb-0">
        <i class="bi bi-exclamation-triangle me-1"></i>{{ periodError }}
      </div>
    }

    <div class="row">
      <div class="col-12 mt-4 text-end">
        <button type="button" class="btn btn-outline-primary me-2" (click)="saveFilters()" title="Save filters">
          <i class="bi bi-floppy"></i>
        </button>
        <button type="button" class="btn btn-outline-secondary me-2" (click)="clearFilters()" title="Clear filters">
          <i class="bi bi-eraser"></i>
        </button>
        <button type="button" class="btn btn-outline-primary fw-bold me-2" [disabled]="loading || !loaded"
          (click)="downloadCsv()"><i class="bi bi-file-earmark-spreadsheet"></i> Export to CSV
        </button>
        <button type="submit" class="btn btn-primary fw-bold" [disabled]="loading || !!periodError"
          (click)="apply()">Apply filters</button>
      </div>
    </div>
  </div>
</div>

@if (loaded) {
  <div class="card border-0 shadow mb-3">
    <div class="card-header bg-tertiary p-3 border-0 d-flex align-items-center">
      <h2 class="fs-6 fw-bold mb-0 me-auto"><i class="bi bi-graph-up me-2"></i>Hourly trend</h2>
      <div class="input-group input-group-sm chart-metric">
        <span class="input-group-text"><i class="bi bi-bar-chart-line"></i></span>
        <select class="form-select" [(ngModel)]="chartMetricKey" (ngModelChange)="changeChartMetric()"
          aria-label="Chart metric">
          @for (group of metricGroups; track group) {
            <optgroup [label]="group">
              @for (metric of getMetricsOfGroup(group); track metric.key) {
                @if (!metric.costBased || hasCost()) {
                  <option [value]="metric.key">{{ metric.label }}</option>
                }
              }
            </optgroup>
          }
        </select>
      </div>
    </div>
    <div class="card-body">
      <div class="period-legend small mb-2">
        <span class="period-badge period-badge-a">A</span>{{ formatPeriod(appliedPeriod) }}
        @if (isComparing()) {
          <span class="period-badge period-badge-b ms-3">B</span>{{ formatPeriod(appliedComparedPeriod) }}
        }
        @if (isAverage()) {
          <span class="badge text-bg-light ms-3">Average per day</span>
        }
      </div>
      <div class="chart-container">
        <canvas baseChart [type]="'line'" [data]="chartData" [options]="chartOptions"></canvas>
      </div>
    </div>
  </div>

  <div class="card border-0 shadow mb-3">
    <div class="card-header fw-bold bg-tertiary p-3 border-0">
      <h2 class="fs-6 fw-bold mb-0"><i class="bi bi-table me-2"></i>Hourly statistics table</h2>
    </div>
    <div class="card-body">
      <div class="metric-picker mb-3">
        @for (group of metricGroups; track group) {
          <div class="metric-picker-group">
            <span class="metric-picker-label">{{ group }}</span>
            @for (metric of getMetricsOfGroup(group); track metric.key) {
              <button type="button" class="btn btn-sm metric-chip"
                [ngClass]="{'metric-chip-active': isMetricSelected(metric)}"
                [disabled]="metric.costBased && !hasCost()"
                [title]="metric.costBased && !hasCost() ? 'Not available when filtering by country or language' : ''"
                (click)="toggleMetric(metric)">
                {{ metric.label }}
              </button>
            }
          </div>
        }
        <button type="button" class="btn btn-sm btn-link" (click)="resetMetrics()">Reset columns</button>
      </div>

      @if (partialProfile) {
        <div class="alert alert-info small py-2">
          <i class="bi bi-info-circle me-1"></i>The period includes today: hours after {{ getCurrentHourLabel() }}
          only contain the previous days.
        </div>
      }

      @if (getVisibleMetrics().length) {
        <div class="table-responsive hourly-table-wrapper">
          <table class="table table-sm table-hover align-middle hourly-table mb-0">
            <thead>
              <tr>
                <th class="hour-column" rowspan="2">{{ isTimeline() ? 'Hour' : 'Hour of the day' }}</th>
                @for (metric of getVisibleMetrics(); track metric.key) {
                  <th class="metric-header text-center" [attr.colspan]="getMetricColspan()"
                    [ngClass]="'metric-group-' + metric.group.toLowerCase()">
                    {{ metric.label }}
                    <a class="chart-link ms-1" role="button" title="Show on the chart" (click)="selectChartMetric(metric)">
                      <i class="bi" [ngClass]="chartMetricKey === metric.key ? 'bi-graph-up-arrow' : 'bi-graph-up'"></i>
                    </a>
                  </th>
                }
              </tr>
              <tr>
                @for (metric of getVisibleMetrics(); track metric.key) {
                  <th class="sub-header text-end" [ngClass]="'metric-group-' + metric.group.toLowerCase() + '-light'">
                    {{ isComparing() ? 'A' : '' }}
                  </th>
                  @if (isComparing()) {
                    <th class="sub-header text-end compared" [ngClass]="'metric-group-' + metric.group.toLowerCase() + '-light'">B</th>
                    <th class="sub-header text-end delta" [ngClass]="'metric-group-' + metric.group.toLowerCase() + '-light'">Δ</th>
                  }
                }
              </tr>
            </thead>

            <tbody>
              @if (isTimeline()) {
                @for (day of days; track day.dayIndex) {
                  <tr class="day-row">
                    <td class="hour-column">
                      <div class="fw-bold">{{ getDayLabel(day) }}</div>
                      @if (isComparing()) {
                        <div class="text-muted small">vs {{ getComparedDayLabel(day) || '—' }}</div>
                      }
                      @if (day.partial) {
                        <div class="text-muted small">until {{ getCurrentHourLabel() }}</div>
                      }
                    </td>
                    @for (metric of getVisibleMetrics(); track metric.key) {
                      <ng-container *ngTemplateOutlet="cellsTemplate; context: {cell: day.cells[metric.key]}"></ng-container>
                    }
                  </tr>
                  @for (slot of day.slots; track slot.hour) {
                    <ng-container *ngTemplateOutlet="slotTemplate; context: {slot: slot}"></ng-container>
                  }
                }
              } @else {
                @for (slot of slots; track slot.hour) {
                  <ng-container *ngTemplateOutlet="slotTemplate; context: {slot: slot}"></ng-container>
                }
              }
            </tbody>

            <tfoot>
              @for (total of totals; track total.label) {
                <tr class="total-row">
                  <td class="hour-column" [title]="total.hint">
                    {{ total.label }}
                    @if (isAverage()) {
                      <div class="text-muted small fw-normal">average per day</div>
                    }
                  </td>
                  @for (metric of getVisibleMetrics(); track metric.key) {
                    <ng-container *ngTemplateOutlet="cellsTemplate; context: {cell: total.cells[metric.key]}"></ng-container>
                  }
                </tr>
              }
            </tfoot>
          </table>
        </div>
      } @else {
        <div class="text-center text-muted py-4">Select at least one column above.</div>
      }
    </div>
  </div>
}

<ng-template #slotTemplate let-slot="slot">
  <tr [ngClass]="'slot-' + slot.state" [title]="getStateTitle(slot)">
    <td class="hour-column" [title]="getSlotTitle(slot)">
      {{ getHourLabel(slot) }}
      @if (slot.state === 'current') {
        <i class="bi bi-hourglass-split ms-1 text-warning"></i>
      }
    </td>
    @for (metric of getVisibleMetrics(); track metric.key) {
      <ng-container *ngTemplateOutlet="cellsTemplate; context: {cell: slot.cells[metric.key]}"></ng-container>
    }
  </tr>
</ng-template>

<ng-template #cellsTemplate let-cell="cell">
  <td class="text-end value">{{ cell?.a }}</td>
  @if (isComparing()) {
    <td class="text-end value compared">{{ cell?.b }}</td>
    <td class="text-end delta" [ngClass]="cell?.trend ? 'delta-' + cell.trend : ''">{{ cell?.delta }}</td>
  }
</ng-template>
